import { ReactElement, ReactNode, forwardRef } from 'react';
import cn from 'classnames';
import { Icon, IconWeight } from '@phosphor-icons/react';

import styles from './TextButton.module.scss';

export type TextButtonType = 'primary' | 'secondary' | 'tertiary' | 'white';
export type TextButtonSize = 'small' | 'medium' | 'large' | 'xlarge';
export type TextButtonPadding = 'compact' | 'default' | 'iconOnly';

/**
 * Extends native button attributes (minus `type`, which is the visual
 * variant here — use `buttonType` for the HTML attribute) so callers like
 * popover/tooltip triggers can pass aria and data props and a ref through.
 */
type TextButtonBase = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> & {
  type?: TextButtonType;
  size?: TextButtonSize;
  padding?: TextButtonPadding;
  leadingIconSize?: number;
  trailingIconSize?: number;
  leadingIconWeight?: IconWeight;
  trailingIconWeight?: IconWeight;
  className?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  buttonType?: 'button' | 'submit' | 'reset';
  'data-testid'?: string;
};

/** Button with visible label; aria-label optional. */
type TextButtonWithLabel = TextButtonBase & {
  label: ReactNode;
  leadingIcon?: Icon;
  trailingIcon?: Icon;
  'aria-label'?: string;
};

/** Icon-only button; aria-label required for accessibility. */
type TextButtonIconOnly = TextButtonBase & {
  label?: never;
  'aria-label': string;
} & (
    | { leadingIcon: Icon; trailingIcon?: Icon }
    | { leadingIcon?: Icon; trailingIcon: Icon }
  );

export type TextButtonProps = TextButtonWithLabel | TextButtonIconOnly;

const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      label,
      type = 'primary',
      size = 'medium',
      padding = 'default',
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      leadingIconSize,
      trailingIconSize,
      leadingIconWeight = 'regular',
      trailingIconWeight = 'regular',
      className,
      disabled = false,
      onClick,
      buttonType = 'button',
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      ...rest
    },
    ref
  ): ReactElement => {
    // Determine if this is icon-only (no label)
    const isIconOnly = !label && (LeadingIcon || TrailingIcon);
    const effectivePadding = isIconOnly ? 'iconOnly' : padding;

    // Default icon sizes based on button size
    const defaultIconSizes: Record<TextButtonSize, number> = {
      small: 16,
      medium: 18,
      large: 20,
      xlarge: 24,
    };
    const effectiveLeadingIconSize = leadingIconSize || defaultIconSizes[size];
    const effectiveTrailingIconSize =
      trailingIconSize || defaultIconSizes[size];

    // For icon-only buttons, use the icon that exists and its weight
    const DisplayIcon = isIconOnly ? LeadingIcon || TrailingIcon : null;
    const iconOnlyWeight = LeadingIcon ? leadingIconWeight : trailingIconWeight;

    const rootClassName = cn(
      styles.textButton,
      styles[type],
      styles[size],
      styles[
        `padding${
          effectivePadding.charAt(0).toUpperCase() + effectivePadding.slice(1)
        }`
      ],
      {
        [styles.disabled]: disabled,
      },
      className
    );

    return (
      <button
        {...rest}
        ref={ref}
        type={buttonType}
        className={rootClassName}
        onClick={onClick}
        disabled={disabled}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        {isIconOnly && DisplayIcon ? (
          <DisplayIcon
            size={effectiveLeadingIconSize}
            weight={iconOnlyWeight}
            aria-hidden="true"
          />
        ) : (
          <>
            {LeadingIcon && (
              <LeadingIcon
                size={effectiveLeadingIconSize}
                weight={leadingIconWeight}
                className={styles.leadingIcon}
                aria-hidden="true"
              />
            )}
            {label && <span className={styles.label}>{label}</span>}
            {TrailingIcon && (
              <TrailingIcon
                size={effectiveTrailingIconSize}
                weight={trailingIconWeight}
                className={styles.trailingIcon}
                aria-hidden="true"
              />
            )}
          </>
        )}
      </button>
    );
  }
);

TextButton.displayName = 'TextButton';

export default TextButton;
